import { metinOku, sayiOku, yuvarla2 } from '../rapor-ekrani/bicim';

/**
 * S350 Araç Satış ve Kârlılık: ds satırlarının ekran karşılıkları. Kolon adları VERİ SÖZLEŞMESİDİR;
 * ham satır burada bir kez okunur, ekran ham alana dokunmaz. Tutarlar KDV dahildir.
 */

export type MaliyetTuru = 'fifo' | 'fifoSon' | 'son';

export const MALIYET_TURU_SECENEKLERI: ReadonlyArray<{ key: MaliyetTuru; label: string }> = [
  { key: 'fifo', label: 'FIFO' },
  { key: 'fifoSon', label: 'FIFO, yoksa son alış' },
  { key: 'son', label: 'Son alış' },
];

/** ds üç maliyeti de satırda verir; tür İSTEMCİDE seçer (sorgu yinelenmez). */
interface HamMaliyetler {
  maliyet_fifo: number | string | null;
  maliyet_fifo_son_alis: number | string | null;
  maliyet_son_alis: number | string | null;
}

type Maliyetler = Record<MaliyetTuru, number>;

const maliyetler = (h: HamMaliyetler): Maliyetler => ({
  fifo: yuvarla2(sayiOku(h.maliyet_fifo)),
  fifoSon: yuvarla2(sayiOku(h.maliyet_fifo_son_alis)),
  son: yuvarla2(sayiOku(h.maliyet_son_alis)),
});

/** Net satışı ve üç maliyeti olan satır (araç ya da ürün): kâr seçili maliyet türüyle hesaplanır. */
export interface KarTabani {
  net: number;
  maliyetler: Maliyetler;
}

export const maliyeti = (s: KarTabani, tur: MaliyetTuru) => s.maliyetler[tur];

/** Kâr = net satış - seçili maliyet, kuruşa yuvarlı. */
export const kari = (s: KarTabani, tur: MaliyetTuru) => yuvarla2(s.net - s.maliyetler[tur]);

/**
 * Kâr %, ERP12'nin kuralıyla (maliyete oranla): kâr 0 ise 0; maliyet 0 değilse kâr / maliyet × 100;
 * maliyet 0 ve kâr artıysa 100, eksiyse -100. Karar EKRANA YAZILAN (kuruşa yuvarlanmış) tutarlardan verilir.
 */
export function karYuzdesi(kar: number, maliyet: number): number {
  const k = yuvarla2(kar);
  const m = yuvarla2(maliyet);
  if (k === 0) return 0;
  if (m !== 0) return (k / m) * 100;
  return k > 0 ? 100 : -100;
}

/** Satırların kâr %'si: satır oranlarının toplamı değil, TOPLAMLARA uygulanan aynı kural. */
export function toplamKarYuzdesi(satirlar: readonly KarTabani[], tur: MaliyetTuru): number {
  const net = satirlar.reduce((t, s) => t + s.net, 0);
  const maliyet = satirlar.reduce((t, s) => t + s.maliyetler[tur], 0);
  return karYuzdesi(net - maliyet, maliyet);
}

/** Tahsilat / net satış (%); net satış (kuruşa yuvarlanınca) 0 ise oran yoktur. */
export function tahsilatOrani(tahsilat: number, net: number): number | null {
  return yuvarla2(net) === 0 ? null : (tahsilat / net) * 100;
}

// ── Liste ───────────────────────────────────────────────────────────────────────

/** `liste` ds'i, parametreler `@BASTARIH`, `@BITTARIH`, `@MOBIL`. Satır = araç (lokasyon). */
export interface HamAracSatiri extends HamMaliyetler {
  lokasyon_id: number | string;
  arac: string | null;
  satis: number | string | null;
  iade: number | string | null;
  net_satis: number | string | null;
  iskonto: number | string | null;
  bedelsiz: number | string | null;
  yedi_gun_satis: number | string | null;
  ay_sonu_net_ciro: number | string | null;
  tahsilat: number | string | null;
}

export interface AracSatiri extends KarTabani {
  /** `lokasyon_id`: çekmecenin `@LOKASYON` değeri ve URL'deki kimlik. */
  id: string;
  ad: string;
  satis: number;
  iade: number;
  iskonto: number;
  bedelsiz: number;
  yediGun: number;
  aySonu: number;
  tahsilat: number;
}

export function aracSatiri(h: HamAracSatiri): AracSatiri {
  return {
    id: String(h.lokasyon_id),
    ad: metinOku(h.arac),
    satis: yuvarla2(sayiOku(h.satis)),
    iade: yuvarla2(sayiOku(h.iade)),
    net: yuvarla2(sayiOku(h.net_satis)),
    iskonto: yuvarla2(sayiOku(h.iskonto)),
    bedelsiz: yuvarla2(sayiOku(h.bedelsiz)),
    maliyetler: maliyetler(h),
    yediGun: yuvarla2(sayiOku(h.yedi_gun_satis)),
    aySonu: yuvarla2(sayiOku(h.ay_sonu_net_ciro)),
    tahsilat: yuvarla2(sayiOku(h.tahsilat)),
  };
}

/** Listedeki BÜTÜN satırlarda 0 olan kolon çizilmez (İade, İskonto, Bedelsiz). */
export const hepsiSifir = (satirlar: readonly AracSatiri[], deger: (s: AracSatiri) => number) =>
  satirlar.every((s) => deger(s) === 0);

// ── Ürünler (çekmece) ───────────────────────────────────────────────────────────

/** Çekmecede gösterilen ürün sayısı (ds de en fazla bu kadar döndürür). */
export const URUN_SINIRI = 25;

/**
 * `urunler` ds'i, parametreler `@LOKASYON`, `@BASTARIH`, `@BITTARIH`, `@MOBIL`: aracın en çok satan
 * ürünleri, net satışa göre azalan.
 */
export interface HamAracUrunSatiri extends HamMaliyetler {
  stok_id: number | string;
  stok_kodu: string | null;
  urun: string | null;
  net_satis: number | string | null;
}

export interface AracUrunSatiri extends KarTabani {
  id: string;
  kod: string;
  ad: string;
}

export function aracUrunSatirlari(ham: readonly HamAracUrunSatiri[]): AracUrunSatiri[] {
  return ham.slice(0, URUN_SINIRI).map((h) => ({
    id: String(h.stok_id),
    kod: metinOku(h.stok_kodu),
    ad: metinOku(h.urun),
    net: yuvarla2(sayiOku(h.net_satis)),
    maliyetler: maliyetler(h),
  }));
}
